import { useState, useEffect, useRef } from 'react';
import { X, Search, Camera, MapPin, User, Building, Mail, Phone, Briefcase, Upload, Check, AlertCircle, Loader2, CheckCircle, Save, FileText, Star, Sparkles, CreditCard, Banknote } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useOCR } from '../../hooks/useOCR';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { AddressAutocomplete, Button } from '../ui';
import { getAllPeople } from '../../services/apiService';
import { addPeopleAsContact, addUserContact, getUserContacts, findPeopleForContact } from '../../services/userContactsService';
import { uploadFile, DOCUMENT_TYPES } from '../../services/filesService';
import { searchOccupationsEsco, selectOccupation } from '../../services/occupationsService';
import { enhanceTextWithAI } from '../../services/geminiService';
import ContactComparisonModal from './ContactComparisonModal';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be/rest/cabaza';

/**
 * Modal for adding a new contact
 * Features:
 * - Tab 1: Business card OCR + basic info (firstName, lastName, email, phone, company, phoneBureau, sexe)
 * - Tab 2: Address with Google Maps autocomplete (all address fields)
 * - Tab 3: Professional info (profileType, occupation, tvaNum, bank, employment status, reputation)
 * - Tab 4: Notes with AI enhancement
 */
const AddContactModal = ({ isOpen, onClose, currentUser, onContactAdded }) => {
    const { t, language } = useTranslation();
    const fileInputRef = useRef(null);

    // Load Google Maps API
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const isGoogleMapsLoaded = useGoogleMaps(googleMapsApiKey);

    // State
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
    const [existingContactIds, setExistingContactIds] = useState(new Set());

    // Profile types state
    const [profileTypes, setProfileTypes] = useState([]);
    const [isLoadingProfileTypes, setIsLoadingProfileTypes] = useState(false);

    // Occupation search state
    const [occupationQuery, setOccupationQuery] = useState('');
    const [occupationResults, setOccupationResults] = useState([]);
    const [isSearchingOccupation, setIsSearchingOccupation] = useState(false);
    const [showOccupationResults, setShowOccupationResults] = useState(false);
    const [selectedOccupation, setSelectedOccupation] = useState(null);

    // AI enhancement state
    const [isEnhancingDescription, setIsEnhancingDescription] = useState(false);

    // State for comparison modal
    const [showComparisonModal, setShowComparisonModal] = useState(false);
    const [foundPeople, setFoundPeople] = useState(null);

    // Form data with all new fields
    const [formData, setFormData] = useState({
        // Tab 1: Basic info (Business card)
        firstName: '',
        lastName: '',
        sexe: '',
        email: '',
        phone: '',
        company: '',
        phoneBureau: '',
        // Tab 2: Address
        googleAddress: '',
        street: '',
        number: '',
        box: '',
        postalCode: '',
        city: '',
        country: '',
        countryCode: '',
        latitude: null,
        longitude: null,
        googlePlaceId: '',
        googleUrl: '',
        nationality: '',
        // Tab 3: Professional
        profileTypeId: '',
        occupationId: null,
        employmentStatus: true, // true = en activité
        tvaNum: '',
        bankName: '',
        bankNum: '',
        reputation: 0,
        // Tab 4: Notes
        description: ''
    });

    // OCR translations
    const ocrTranslations = {
        ocr: {
            processing: t.contacts?.businessCard?.processing || 'Processing...',
            success: (count) => `${count} ${t.contacts?.businessCard?.fieldsCompleted || 'fields extracted'}`,
            noInfo: t.contacts?.businessCard?.noInfo || 'No information extracted',
            error: t.contacts?.businessCard?.error || 'Error during analysis',
            unavailable: t.contacts?.businessCard?.unavailable || 'OCR unavailable',
            invalidImage: t.ocr?.invalidImage || 'Invalid image file'
        }
    };

    // OCR hook
    const { isProcessing, imagePreview, message: ocrMessage, progress, processImage, clearImage } = useOCR(language, ocrTranslations);

    // Sexe options
    const sexeOptions = [
        { value: '', label: t.contacts?.fields?.genderOptions?.unspecified || 'Not specified' },
        { value: 'M', label: t.contacts?.fields?.genderOptions?.male || 'Male' },
        { value: 'F', label: t.contacts?.fields?.genderOptions?.female || 'Female' },
        { value: 'O', label: t.contacts?.fields?.genderOptions?.other || 'Other' }
    ];

    // Load profile types
    const loadProfileTypes = async () => {
        setIsLoadingProfileTypes(true);
        try {
            const response = await fetch(`${API_BASE_URL}/profiletypes`);
            const data = await response.json();
            const types = data.data ?? data.DATA ?? data ?? [];
            setProfileTypes(Array.isArray(types) ? types : []);
        } catch (error) {
            console.error('Error loading profile types:', error);
            setProfileTypes([]);
        } finally {
            setIsLoadingProfileTypes(false);
        }
    };

    // Load existing contacts and reset form when modal opens
    useEffect(() => {
        if (isOpen && currentUser?.id) {
            loadProfileTypes();

            // Load existing contacts to mark them
            const loadExistingContacts = async () => {
                try {
                    const contacts = await getUserContacts(currentUser.id);
                    const ids = new Set(contacts.map(c => c.peopleuuid).filter(Boolean));
                    setExistingContactIds(ids);
                } catch (error) {
                    console.error('Error loading existing contacts:', error);
                }
            };
            loadExistingContacts();

            // Reset form
            setActiveTab(0);
            setSearchQuery('');
            setSearchResults([]);
            setShowResults(false);
            setSelectedFile(null);
            setSaveMessage({ type: '', text: '' });
            setShowComparisonModal(false);
            setFoundPeople(null);
            setOccupationQuery('');
            setOccupationResults([]);
            setSelectedOccupation(null);
            clearImage();
            setFormData({
                firstName: '',
                lastName: '',
                sexe: '',
                email: '',
                phone: '',
                company: '',
                phoneBureau: '',
                googleAddress: '',
                street: '',
                number: '',
                box: '',
                postalCode: '',
                city: '',
                country: '',
                countryCode: '',
                latitude: null,
                longitude: null,
                googlePlaceId: '',
                googleUrl: '',
                nationality: '',
                profileTypeId: '',
                occupationId: null,
                employmentStatus: true,
                tvaNum: '',
                bankName: '',
                bankNum: '',
                reputation: 0,
                description: ''
            });
        }
    }, [isOpen, currentUser?.id]);

    // Search existing people
    const handleSearch = async (query) => {
        setSearchQuery(query);
        if (query.length < 2) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }
        setIsSearching(true);
        setShowResults(true);
        try {
            const results = await getAllPeople({ keyword: query, limit: 10 });
            setSearchResults(results || []);
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Search occupations via ESCO API
    const handleOccupationSearch = async (query) => {
        setOccupationQuery(query);
        if (query.length < 2) {
            setOccupationResults([]);
            setShowOccupationResults(false);
            return;
        }
        setIsSearchingOccupation(true);
        setShowOccupationResults(true);
        try {
            const results = await searchOccupationsEsco(query, language, 10);
            setOccupationResults(results || []);
        } catch (error) {
            console.error('Occupation search error:', error);
            setOccupationResults([]);
        } finally {
            setIsSearchingOccupation(false);
        }
    };

    // Select an occupation
    const handleSelectOccupation = async (occupation) => {
        try {
            const savedOccupation = await selectOccupation(occupation);
            setSelectedOccupation(savedOccupation);
            setFormData(prev => ({ ...prev, occupationId: savedOccupation.id }));
            setOccupationQuery(occupation.displayLabel || occupation.title);
            setShowOccupationResults(false);
        } catch (error) {
            console.error('Error selecting occupation:', error);
        }
    };

    // Check if a person is already a contact
    const isAlreadyContact = (person) => {
        const peopleuuid = person.peopleUuid || person.peopleuuid || person.PEOPLEUUID;
        return peopleuuid && existingContactIds.has(peopleuuid);
    };

    // Add existing person as contact
    const handleAddExistingContact = async (person) => {
        if (!currentUser?.id) return;
        setIsSaving(true);
        try {
            const result = await addPeopleAsContact(currentUser.id, person);
            if (result) {
                setSaveMessage({ type: 'success', text: t.contacts?.contactAdded || 'Contact added successfully!' });
                onContactAdded?.(result);
                window.dispatchEvent(new CustomEvent('contactAdded', { detail: result }));
                setTimeout(() => onClose(), 1500);
            } else {
                setSaveMessage({ type: 'error', text: t.contacts?.errorAdding || 'Error adding contact' });
            }
        } catch (error) {
            setSaveMessage({ type: 'error', text: error.message });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle file selection for business card OCR
    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFile(file);
        const extractedData = await processImage(file);
        if (extractedData) {
            setFormData(prev => ({
                ...prev,
                firstName: extractedData.firstName || prev.firstName,
                lastName: extractedData.lastName || prev.lastName,
                email: extractedData.email || prev.email,
                phone: extractedData.phone || prev.phone,
                company: extractedData.company || prev.company,
                phoneBureau: extractedData.phoneBureau || extractedData.phone2 || prev.phoneBureau,
                googleAddress: extractedData.address || prev.googleAddress
            }));
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle address selection from Google autocomplete
    const handleAddressSelect = (addressData) => {
        setFormData(prev => ({
            ...prev,
            googleAddress: addressData.formatted_address || addressData.address || '',
            street: addressData.street || '',
            number: addressData.number || '',
            postalCode: addressData.postalCode || addressData.zip || '',
            city: addressData.city || '',
            country: addressData.country || '',
            countryCode: addressData.countryCode || '',
            latitude: addressData.latitude || null,
            longitude: addressData.longitude || null,
            googlePlaceId: addressData.placeId || '',
            googleUrl: addressData.url || ''
        }));
    };

    // Handle coordinates from address autocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    };

    // Handle address components from Google
    const handleAddressComponentsChange = (components) => {
        if (components) {
            setFormData(prev => ({
                ...prev,
                street: components.street || prev.street,
                number: components.number || prev.number,
                postalCode: components.zip || components.postalCode || prev.postalCode,
                city: components.city || prev.city,
                country: components.country || prev.country,
                countryCode: components.countryCode || prev.countryCode
            }));
        }
    };

    // Enhance description with AI
    const handleEnhanceDescription = async () => {
        if (!formData.description.trim()) return;
        setIsEnhancingDescription(true);
        try {
            const enhanced = await enhanceTextWithAI(formData.description, language);
            if (enhanced) {
                setFormData(prev => ({ ...prev, description: enhanced }));
            }
        } catch (error) {
            console.error('Error enhancing description:', error);
        } finally {
            setIsEnhancingDescription(false);
        }
    };

    // Save contact
    const handleSave = async () => {
        if (!currentUser?.id) return;

        if (!formData.firstName && !formData.lastName && !formData.email) {
            setSaveMessage({
                type: 'error',
                text: t.validation?.required || 'At least a name or email required'
            });
            return;
        }

        // If no complete info for search, create private contact directly
        if (!formData.firstName || !formData.lastName || (!formData.email && !formData.phone)) {
            await createPrivateContact();
            return;
        }

        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            const existingPeople = await findPeopleForContact({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone
            });

            if (existingPeople) {
                setFoundPeople(existingPeople);
                setShowComparisonModal(true);
                setIsSaving(false);
            } else {
                await createPrivateContact();
            }
        } catch (error) {
            console.error('Save error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (t.contacts?.errorSearching || 'Error during search')
            });
            setIsSaving(false);
        }
    };

    // Create private contact
    const createPrivateContact = async () => {
        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            const contactData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                sexe: formData.sexe,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                phoneBureau: formData.phoneBureau,
                googleAddress: formData.googleAddress,
                street: formData.street,
                number: formData.number,
                box: formData.box,
                postalCode: formData.postalCode,
                city: formData.city,
                country: formData.country,
                countryCode: formData.countryCode,
                latitude: formData.latitude,
                longitude: formData.longitude,
                googlePlaceId: formData.googlePlaceId,
                googleUrl: formData.googleUrl,
                nationality: formData.nationality,
                profileTypeId: formData.profileTypeId || null,
                occupationId: formData.occupationId,
                employmentStatus: formData.employmentStatus,
                tvaNum: formData.tvaNum,
                bankName: formData.bankName,
                bankNum: formData.bankNum,
                reputation: formData.reputation,
                notes: formData.description
            };

            const contactResult = await addUserContact(currentUser.id, contactData);

            if (contactResult) {
                // Upload business card if present
                if (selectedFile && contactResult.localuuid) {
                    try {
                        await uploadFile(selectedFile, {
                            path: `contacts/${contactResult.localuuid}/`,
                            fileType: DOCUMENT_TYPES.BUSINESS_CARD,
                            entityType: 'contacts',
                            entityId: contactResult.id,
                            entityUuid: contactResult.localuuid,
                            uploadedById: currentUser.id,
                            description: 'Business card'
                        });
                    } catch (uploadError) {
                        console.error('Error uploading business card:', uploadError);
                    }
                }

                setSaveMessage({
                    type: 'success',
                    text: t.contacts?.contactCreated || 'Contact created successfully!'
                });
                onContactAdded?.(contactResult);
                window.dispatchEvent(new CustomEvent('contactAdded', { detail: contactResult }));
                setTimeout(() => onClose(), 1500);
            } else {
                throw new Error('Failed to create contact');
            }
        } catch (error) {
            console.error('Create contact error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (t.contacts?.errorCreating || 'Error creating contact')
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle confirmation from comparison modal
    const handleComparisonConfirm = async (mergedData) => {
        setShowComparisonModal(false);
        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            const contactData = {
                peopleuuid: mergedData.peopleuuid,
                firstName: mergedData.firstName,
                lastName: mergedData.lastName,
                sexe: formData.sexe,
                email: mergedData.email,
                phone: mergedData.phone,
                company: mergedData.company,
                phoneBureau: formData.phoneBureau,
                googleAddress: mergedData.googleAddress || formData.googleAddress,
                street: formData.street,
                number: formData.number,
                box: formData.box,
                postalCode: formData.postalCode,
                city: formData.city,
                country: formData.country,
                countryCode: formData.countryCode,
                latitude: formData.latitude,
                longitude: formData.longitude,
                googlePlaceId: formData.googlePlaceId,
                googleUrl: formData.googleUrl,
                nationality: formData.nationality,
                profileTypeId: formData.profileTypeId || null,
                occupationId: formData.occupationId,
                employmentStatus: formData.employmentStatus,
                tvaNum: formData.tvaNum,
                bankName: formData.bankName,
                bankNum: formData.bankNum,
                reputation: formData.reputation,
                notes: formData.description
            };

            const contactResult = await addUserContact(currentUser.id, contactData);

            if (contactResult) {
                if (selectedFile && contactResult.localuuid) {
                    try {
                        await uploadFile(selectedFile, {
                            path: `contacts/${contactResult.localuuid}/`,
                            fileType: DOCUMENT_TYPES.BUSINESS_CARD,
                            entityType: 'contacts',
                            entityId: contactResult.id,
                            entityUuid: contactResult.localuuid,
                            uploadedById: currentUser.id,
                            description: 'Business card'
                        });
                    } catch (uploadError) {
                        console.error('Error uploading business card:', uploadError);
                    }
                }

                setSaveMessage({
                    type: 'success',
                    text: t.contacts?.publicContactCreated || 'Public contact created successfully!'
                });
                onContactAdded?.(contactResult);
                window.dispatchEvent(new CustomEvent('contactAdded', { detail: contactResult }));
                setTimeout(() => onClose(), 1500);
            } else {
                throw new Error('Failed to create contact');
            }
        } catch (error) {
            console.error('Create public contact error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (t.contacts?.errorCreating || 'Error creating contact')
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Get profile type label
    const getProfileTypeLabel = (type) => {
        const langKey = `name_${language}`;
        return type[langKey] || type.name_fr || type.name_en || type.code || '';
    };

    // Tab definitions
    const tabs = [
        { id: 0, icon: Camera, label: t.contacts?.tabs?.businessCard || 'Business Card' },
        { id: 1, icon: MapPin, label: t.contacts?.tabs?.address || 'Address' },
        { id: 2, icon: Briefcase, label: t.contacts?.tabs?.professional || 'Professional' },
        { id: 3, icon: FileText, label: t.contacts?.tabs?.notes || 'Notes' }
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-4 flex items-center justify-between">
                    <h2 className="text-white text-lg font-semibold">
                        {t.contacts?.addContact || 'Add Contact'}
                    </h2>
                    <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded-full transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search existing people */}
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder={t.contacts?.searchExisting || 'Search existing contact...'}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                        />
                        {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cabaza-purple animate-spin" />
                        )}
                    </div>

                    {/* Search results */}
                    {showResults && searchResults.length > 0 && (
                        <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                            {searchResults.map((person) => {
                                const personKey = person.peopleUuid || person.peopleuuid || person.id || person.ID;
                                const alreadyContact = isAlreadyContact(person);
                                return (
                                    <div
                                        key={personKey}
                                        className={`flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0 ${
                                            alreadyContact ? 'bg-green-50 cursor-default' : 'hover:bg-gray-50 cursor-pointer'
                                        }`}
                                        onClick={() => !alreadyContact && handleAddExistingContact(person)}
                                    >
                                        <div className="flex items-center gap-3">
                                            {alreadyContact && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                                            <div>
                                                <div className={`font-medium ${alreadyContact ? 'text-green-700' : 'text-gray-900'}`}>
                                                    {person.firstName} {person.lastName}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {person.email || person.company || ''}
                                                </div>
                                            </div>
                                        </div>
                                        {alreadyContact ? (
                                            <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-100 rounded-full">
                                                {t.contacts?.alreadyContact || 'Already contact'}
                                            </span>
                                        ) : (
                                            <button className="text-cabaza-purple hover:bg-cabaza-purple/10 p-2 rounded-lg transition">
                                                <Check className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                        <div className="mt-2 text-sm text-gray-500 text-center py-2">
                            {t.contacts?.noContactFound || 'No contact found'}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-2 text-sm font-medium transition ${
                                activeTab === tab.id
                                    ? 'text-cabaza-purple border-b-2 border-cabaza-purple bg-purple-50'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="p-4 overflow-y-auto max-h-[50vh]">
                    {/* Tab 0: Business Card & Basic Info */}
                    {activeTab === 0 && (
                        <div className="space-y-4">
                            {/* Upload area */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-cabaza-purple transition"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                {imagePreview ? (
                                    <div className="space-y-2">
                                        <img src={imagePreview} alt="Business card" className="max-h-32 mx-auto rounded-lg shadow" />
                                        {isProcessing && (
                                            <div className="flex items-center justify-center gap-2 text-cabaza-purple">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>{ocrMessage.text} ({progress}%)</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="w-10 h-10 mx-auto text-gray-400" />
                                        <p className="mt-1 text-sm text-gray-600">
                                            {t.contacts?.businessCard?.upload || 'Business card photo'}
                                        </p>
                                    </>
                                )}
                            </div>

                            {/* OCR message */}
                            {ocrMessage.text && !isProcessing && (
                                <div className={`p-2 rounded-lg text-sm ${
                                    ocrMessage.type === 'success' ? 'bg-green-50 text-green-700' :
                                    ocrMessage.type === 'error' ? 'bg-red-50 text-red-700' :
                                    'bg-blue-50 text-blue-700'
                                }`}>
                                    {ocrMessage.text}
                                </div>
                            )}

                            {/* Basic fields */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.firstName || 'First Name'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.lastName || 'Last Name'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.gender || 'Gender'}
                                    </label>
                                    <select
                                        value={formData.sexe}
                                        onChange={(e) => handleFieldChange('sexe', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    >
                                        {sexeOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">{t.contacts?.fields?.email || 'Email'}</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleFieldChange('email', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.phoneMobile || 'Phone'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.phoneFixed || 'Office Phone'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phoneBureau}
                                        onChange={(e) => handleFieldChange('phoneBureau', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.contacts?.fields?.company || 'Company'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => handleFieldChange('company', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>
                        </div>
                    )}

                    {/* Tab 1: Address */}
                    {activeTab === 1 && (
                        <div className="space-y-4">
                            {isGoogleMapsLoaded ? (
                                <AddressAutocomplete
                                    label={t.register?.address?.searchAddress || 'Search address'}
                                    value={formData.googleAddress}
                                    onChange={(field, value) => handleFieldChange('googleAddress', value)}
                                    onCoordinatesChange={handleCoordinatesChange}
                                    onAddressComponentsChange={handleAddressComponentsChange}
                                    apiKey={googleMapsApiKey}
                                />
                            ) : (
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.tabs?.address || 'Address'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.googleAddress}
                                        onChange={(e) => handleFieldChange('googleAddress', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.street || 'Street'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.street}
                                        onChange={(e) => handleFieldChange('street', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">{t.contacts?.fields?.number || 'N°'}</label>
                                    <input
                                        type="text"
                                        value={formData.number}
                                        onChange={(e) => handleFieldChange('number', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.box || 'Box'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.box}
                                        onChange={(e) => handleFieldChange('box', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.postalCode || 'Postal Code'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.postalCode}
                                        onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.city || 'City'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => handleFieldChange('city', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.contacts?.fields?.country || 'Country'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.country}
                                        onChange={(e) => handleFieldChange('country', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.register?.address?.nationality || 'Nationality'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.nationality}
                                        onChange={(e) => handleFieldChange('nationality', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            {formData.latitude && formData.longitude && (
                                <div className="text-sm text-green-600 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    {t.register?.address?.gpsRecorded || 'GPS coordinates saved'}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 2: Professional */}
                    {activeTab === 2 && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.register?.profileType?.sectionTitle || 'Profile Type'}
                                    </label>
                                    <select
                                        value={formData.profileTypeId}
                                        onChange={(e) => handleFieldChange('profileTypeId', e.target.value)}
                                        disabled={isLoadingProfileTypes}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    >
                                        <option value="">{t.common?.select || 'Select...'}</option>
                                        {profileTypes.map(type => (
                                            <option key={type.id || type.ID} value={type.id || type.ID}>
                                                {getProfileTypeLabel(type)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.register?.employmentStatus?.title || 'Status'}
                                    </label>
                                    <select
                                        value={formData.employmentStatus ? 'active' : 'inactive'}
                                        onChange={(e) => handleFieldChange('employmentStatus', e.target.value === 'active')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    >
                                        <option value="active">{t.endroits?.active || 'Active'}</option>
                                        <option value="inactive">{t.products?.inactive || 'Inactive'}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Occupation search */}
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.contacts?.fields?.profession || 'Occupation'}
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={occupationQuery}
                                        onChange={(e) => handleOccupationSearch(e.target.value)}
                                        placeholder={t.register?.professional?.professionPlaceholder || 'Search occupation...'}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                    {isSearchingOccupation && (
                                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cabaza-purple animate-spin" />
                                    )}
                                </div>
                                {showOccupationResults && occupationResults.length > 0 && (
                                    <div className="mt-1 border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
                                        {occupationResults.map((occ, idx) => (
                                            <div
                                                key={occ.uri || idx}
                                                className="p-2 hover:bg-gray-50 cursor-pointer text-sm"
                                                onClick={() => handleSelectOccupation(occ)}
                                            >
                                                {occ.displayLabel || occ.title}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedOccupation && (
                                    <div className="mt-1 text-sm text-green-600 flex items-center gap-1">
                                        <Check className="w-4 h-4" />
                                        {t.common?.success || 'Selected'}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.endroits?.tvaNum || 'VAT Number'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.tvaNum}
                                    onChange={(e) => handleFieldChange('tvaNum', e.target.value)}
                                    placeholder="BE0123456789"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                                        {t.common?.bank || 'Bank'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.bankName}
                                        onChange={(e) => handleFieldChange('bankName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">IBAN</label>
                                    <input
                                        type="text"
                                        value={formData.bankNum}
                                        onChange={(e) => handleFieldChange('bankNum', e.target.value)}
                                        placeholder="BE00 0000 0000 0000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">
                                    {t.common?.reputation || 'Reputation'}
                                </label>
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => handleFieldChange('reputation', star)}
                                            className="p-1"
                                        >
                                            <Star
                                                className={`w-6 h-6 ${
                                                    star <= formData.reputation
                                                        ? 'text-yellow-400 fill-yellow-400'
                                                        : 'text-gray-300'
                                                }`}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab 3: Notes */}
                    {activeTab === 3 && (
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        {t.contacts?.fields?.notes || 'Notes'}
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleEnhanceDescription}
                                        disabled={isEnhancingDescription || !formData.description.trim()}
                                        className="flex items-center gap-1 text-xs text-cabaza-purple hover:text-cabaza-pink disabled:opacity-50"
                                    >
                                        {isEnhancingDescription ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <Sparkles className="w-3 h-3" />
                                        )}
                                        {t.contacts?.ai?.enhance || 'Enhance with AI'}
                                    </button>
                                </div>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    rows={8}
                                    placeholder={t.contacts?.fields?.notesPlaceholder || 'Personal notes, contact description...'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple resize-none"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    {saveMessage.text && (
                        <div className={`mb-3 p-3 rounded-lg text-sm flex items-center gap-2 ${
                            saveMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                            {saveMessage.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {saveMessage.text}
                        </div>
                    )}

                    <div className="flex gap-3">
                        <Button variant="outline" onClick={onClose} fullWidth>
                            {t.contacts?.actions?.cancel || t.common?.cancel || 'Cancel'}
                        </Button>
                        <Button onClick={handleSave} disabled={isProcessing} loading={isSaving} icon={Save} fullWidth>
                            {t.contacts?.actions?.save || t.common?.save || 'Save'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Comparison Modal */}
            <ContactComparisonModal
                isOpen={showComparisonModal}
                onClose={() => setShowComparisonModal(false)}
                formData={formData}
                peopleData={foundPeople}
                onConfirm={handleComparisonConfirm}
            />
        </div>
    );
};

export default AddContactModal;
